/**
 * DORA's vocabulary (P415).
 *
 * The one regime named after an INSTRUMENT rather than a regulator,
 * because Regulation (EU) 2022/2554 is enforced by the three ESAs
 * together with national competent authorities and naming any one of
 * them would be a false claim about who supervises.
 *
 * `regulatorShort` is therefore "ESAs" and `examinerTerm` is
 * "competent authority" -- what a supervised entity actually deals
 * with, which in practice is its national regulator, not a European
 * body.
 *
 * DORA speaks of *critical or important functions* and *ICT services*.
 * It does not use "important business service", so this overlay does
 * not either.
 *
 * English only, matching the regime's seeded `languages`. EUR-Lex
 * publishes 24 languages and none is subordinate, so a second entry
 * here would be a claim about precedence nobody has made (P414's
 * reasoning, unchanged).
 */
import type { RegimeVocab, RegimeVocabByLanguage } from "shared/i18n/regime/types";

const en: RegimeVocab = {
  serviceTerm: "critical or important function",
  serviceTermPlural: "critical or important functions",
  serviceTermShort: "critical function",
  serviceTermShortPlural: "critical functions",
  serviceTermShortA: "a critical function",
  serviceTermTitle: "Critical or important function",
  serviceTermPluralTitle: "Critical or important functions",
  serviceTermDefinition:
    "A function whose disruption would materially impair the financial " +
    "performance of the entity, or the soundness or continuity of its " +
    "services and activities.",

  toleranceTerm: "impact tolerance",
  toleranceTermPlural: "impact tolerances",
  toleranceTermTitle: "Impact tolerance",
  riskLimitTerm: "risk tolerance level",

  institutionTerm: "entity",
  institutionTermPlural: "entities",
  institutionClassTerm: "financial entity",
  regulatorName: "European Supervisory Authorities and national competent authorities",
  regulatorShort: "ESAs",
  examinerTerm: "competent authority",
  letterTerm: "supervisory communication",

  mvbTerm: "minimum set of functions",
  mvbTermTitle: "Minimum set of functions",
  mvbTermShort: "minimum set",

  admissionPathway: {
    ces: "not used under this regime",
    ids: "not used under this regime",
    critical_operation: "Critical function identification",
  },

  outcomeCategory: {
    minimize_customer_harm: "Minimise impact on clients",
    retain_customer_confidence: "Retain confidence in the entity",
    regulatory_engagement: "Meet supervisory obligations",
    protect_safety_and_soundness: "Protect soundness and continuity",
  },
  outcomeCategoryDefinition: {
    minimize_customer_harm:
      "Disruption would cause clients financial loss or deny them access " +
      "to services they depend on.",
    retain_customer_confidence:
      "Disruption would damage confidence in the entity or in the market " +
      "infrastructure it participates in.",
    regulatory_engagement:
      "Disruption would breach a reporting or supervisory obligation " +
      "under the Regulation.",
    protect_safety_and_soundness:
      "Disruption would materially impair the entity's financial " +
      "performance or the continuity of its services.",
  },

  boardPackTitle: "Management body resilience snapshot",
  currencySymbol: "EUR",
};

export const dora: RegimeVocabByLanguage = { en };
